import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRANDS, money } from "@/lib/brands";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClientPetPicker } from "@/components/ClientPetPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Download } from "lucide-react";

export function ServiceList({ kind, title, icon: Icon, pdfUrlFn, renderMeta, newPath, editPath }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pmap, setPmap] = useState({});
  const [statusF, setStatusF] = useState("all");
  const [brandF, setBrandF] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF !== "all") params.status = statusF;
      if (user?.role === "admin" && brandF !== "all") params.brand = brandF;
      const { data } = await api.get(`/${kind}`, { params });
      setItems(data);
      const ids = [...new Set(data.map((r) => r.patient_id))];
      if (ids.length) {
        const { data: pts } = await api.get("/patients", { params: user?.role === "admin" && brandF !== "all" ? { brand: brandF } : {} });
        const m = {}; pts.forEach((p) => (m[p.id] = p)); setPmap(m);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF, brandF]);

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2"><Icon className="h-6 w-6 text-primary" /><h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">{title}</h1></div>
          <p className="text-sm text-muted-foreground mt-1">Open bookings are editable; close with the final amount to generate the PDF.</p>
        </div>
        <ClientPetPicker
          trigger={<Button data-testid={`new-${kind}-btn`}><Plus className="h-4 w-4 mr-2" />New {title}</Button>}
          onPick={(p) => navigate(`${newPath}?patient=${p.id}`)}
        />
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-40" data-testid={`${kind}-status-filter`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        {user?.role === "admin" && (
          <Select value={brandF} onValueChange={setBrandF}>
            <SelectTrigger className="w-44" data-testid={`${kind}-brand-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {BRANDS.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card className="p-0 border-border/60 overflow-hidden">
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-12 text-center text-muted-foreground"><Icon className="h-10 w-10 mx-auto opacity-40 mb-3" />No {title.toLowerCase()} bookings yet.</div>}
        <div className="divide-y divide-border">
          {items.map((r) => {
            const p = pmap[r.patient_id] || {};
            return (
              <div key={r.id} data-testid={`${kind}-row-${r.id}`} className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-muted/40 transition-colors">
                <button onClick={() => navigate(editPath(r.id))} className="text-left min-w-0 flex-1">
                  <div className="font-medium truncate">{p.pet_name || "—"} <span className="text-muted-foreground font-normal">· {p.owner_name || ""}</span></div>
                  <div className="text-xs text-muted-foreground">{renderMeta(r, p)}</div>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold">{money(r.amount_paid)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "closed" ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{r.status === "closed" ? "Closed" : "Open"}</span>
                  <a href={pdfUrlFn(r.id)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid={`${kind}-pdf-${r.id}`}><Download className="h-4 w-4" /></Button></a>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
